/**
 * The icon vocabulary (docs/specs/2026-09-24-icon-vocabulary.md): one icon
 * per concept, per frame type and per state. Code that shows a concept, a
 * frame type or a state with an icon takes it from these maps.
 */
import type { LucideIcon } from "lucide-react";
import type { FrameType } from "@/api/spaces";
import { Icons, type IconName } from "./icons";

/**
 * The spec's "Things" table. Every value is a LucideIcon except `space`, the
 * custom SpaceIcon (an SVG component with the same `className` contract).
 */
export const CONCEPT_ICONS = {
  chat: Icons.MessageCircle,
  comment: Icons.MessageSquare,
  task: Icons.ListTodo,
  taskBoard: Icons.Kanban,
  frame: Icons.Frame,
  workspace: Icons.LayoutGrid,
  space: Icons.Space,
  project: Icons.FolderKanban,
  folder: Icons.Folder,
  browse: Icons.FolderTree,
  agent: Icons.Bot,
  team: Icons.Users,
  memory: Icons.Network,
  skill: Icons.BookOpen,
  askDenker: Icons.Sparkles,
  app: Icons.Plug,
  gitBranch: Icons.GitBranch,
  notification: Icons.Bell,
  search: Icons.Search,
  history: Icons.Clock,
  settings: Icons.Settings,
  voice: Icons.Mic,
  speakOn: Icons.Volume2,
  speakOff: Icons.VolumeX,
  approval: Icons.Shield,
} as const satisfies Record<string, (typeof Icons)[IconName]>;

/** States, for frames, cards and banners; a list row shows a status dot. */
export const STATE_ICONS = {
  error: Icons.CircleAlert,
  warning: Icons.AlertTriangle,
  done: Icons.CheckCircle,
  selected: Icons.Check,
} as const satisfies Record<string, LucideIcon>;

/** Every frame type's icon. The app's only frame-type icon map. */
export const FRAME_TYPE_ICONS: Record<FrameType, LucideIcon> = {
  markdown: Icons.FileText,
  html: Icons.Globe,
  question: Icons.HelpCircle,
  code: Icons.Code,
  search: CONCEPT_ICONS.search,
  workflow: Icons.Workflow,
  image: Icons.Image,
  email: Icons.Send,
  newsletter: Icons.Mail,
  connection: CONCEPT_ICONS.app,
  connection_prompt: CONCEPT_ICONS.app,
  memory: CONCEPT_ICONS.memory,
  note: Icons.StickyNote,
  doc: Icons.StickyNote,
  conversation: CONCEPT_ICONS.chat,
  linkedin: Icons.Linkedin,
  agent_list: CONCEPT_ICONS.team,
  agent_settings: CONCEPT_ICONS.settings,
  taskboard: CONCEPT_ICONS.taskBoard,
  github: Icons.Github,
  event: Icons.Calendar,
  agenda: Icons.CalendarClock,
  flight: Icons.Send,
  weather: Icons.Cloud,
  chart: Icons.BarChart2,
  dataset: Icons.Table,
  preview_card: Icons.Image,
  link_preview: Icons.ExternalLink,
  chat: CONCEPT_ICONS.chat,
  slack: Icons.Hash,
  todo: CONCEPT_ICONS.task,
  board: CONCEPT_ICONS.taskBoard,
  music: Icons.Play,
  calendar: Icons.Calendar,
  file: Icons.FileText,
  terminal: Icons.Terminal,
};

function isFrameType(type: string): type is FrameType {
  return Object.prototype.hasOwnProperty.call(FRAME_TYPE_ICONS, type);
}

/** A frame type's icon; an unknown or missing type gets the generic Frame. */
export function getFrameTypeIcon(type: string | null | undefined): LucideIcon {
  return type && isFrameType(type)
    ? FRAME_TYPE_ICONS[type]
    : CONCEPT_ICONS.frame;
}
